<script setup>
import { useBreakpoints } from '@vueuse/core'
import {computed, onMounted, reactive, ref} from 'vue'
import { Carousel, Navigation, Slide } from 'vue3-carousel'

import FAQAccordionV2 from '@/components/FAQAccordionV2.vue'
import InputPricingColumn from '@/components/form/InputPricingColumn.vue'
import LimitedEditions from '@/components/LimitedEditions.vue'
import { useFormDataStore } from '@/stores/FormDataStore'
import { useSubscriptionsStore } from '@/stores/SubscriptionsStore'
import { useHelpers } from '@/useHelpers'

import ForbesDNA from '../components/ForbesDNA.vue'
import IconNavigation from '../components/icons/IconNavigation.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
})

const state = reactive({
  isNavigationVisible: false,
  carouselBreakPoints: {}
})

const dataStore = useFormDataStore()

const subscriptionsStore = useSubscriptionsStore()

const subscriptionTypes = computed(() => subscriptionsStore.all.filter(subscription => !subscription.bundle.length))
const bundledSubscriptionTypes = computed(() => subscriptionsStore.all.filter(subscription => subscription.bundle.length))


const { getI18nCarouselOptions } = useHelpers()

const selectedSubscription = computed({
  get() {
    return props.modelValue
  },
  set(newSelected) {
    dataStore.setSelectedSubscription(newSelected)
    emits('update:modelValue', newSelected)
  }
})

const breakpoints = useBreakpoints({
  mobile: 0,
  desktop: 992
})

const setCarouselBreakPoints = () => {

  let mobileItemsToShow = 1.2
  let desktopItemsToShow = 3
  if (subscriptionTypes.value.length === 1) {
    mobileItemsToShow = 1
    desktopItemsToShow = 1
  }

  if (subscriptionTypes.value.length === 2) {
    desktopItemsToShow = 2
  }

  state.carouselBreakPoints = {
    1: {
      itemsToShow: mobileItemsToShow,
      snapAlign: 'center'
    },
    992: {
      itemsToShow: desktopItemsToShow,
      snapAlign: 'start'
    }
  }
}

const setIsNavigationVisible = () => {
  if (subscriptionTypes.value.length === 1) {
    state.isNavigationVisible = false
    return
  }

  if (subscriptionTypes.value.length <= 3 && breakpoints.desktop.value) {
    state.isNavigationVisible = false
    return
  }

  state.isNavigationVisible = true
}

const currentSlide = ref(0)

const handleSlideChange = (slide) => currentSlide.value === slide

const emits = defineEmits(['update:modelValue', 'onWhatContainsClick'])

window.addEventListener('resize', () => {
  setIsNavigationVisible()
})

onMounted(function() {
  setIsNavigationVisible()
  setCarouselBreakPoints()
})
</script>

<template>
  <slot />
  <Carousel
    class="pricing-page__cards"
    :transition="600"
    v-model="currentSlide"
    :i18n="getI18nCarouselOptions()"
    :breakpoints="state.carouselBreakPoints"
  >
    <template #addons>
      <Navigation v-if="state.isNavigationVisible">
        <template #next>
          <IconNavigation />
        </template>
        <template #prev>
          <IconNavigation :mirrored="true" />
        </template>
      </Navigation>
    </template>
    <template #slides>
      <Slide v-for="subscription in subscriptionTypes" :key="subscription.id">
        <InputPricingColumn
          :data="subscription"
          v-model="selectedSubscription"
          @on-what-contains-click="(payload) => emits('onWhatContainsClick', payload)"
        />
      </Slide>
    </template>
  </Carousel>

  <div
      v-if="state.isNavigationVisible"
      class="pricing-page__forbes-dna"
  >
    <div class="forbes-dna-card">
      <div class="forbes-dna-card__pagination">
        <button
          v-for="slide in subscriptionTypes.length"
          :key="slide"
          :data-key="slide"
          class="forbes-dna-card__pagination-dot"
          :class="{
            'forbes-dna-card__pagination-dot--active': slide - 1 === currentSlide
          }"
          @click="handleSlideChange(slide - 1)"
        />
      </div>
    </div>
  </div>

  <div class="pricing-page__section-divider"></div>
  <LimitedEditions
      v-if="bundledSubscriptionTypes"
      v-model="selectedSubscription"
      :subscriptions="bundledSubscriptionTypes"
  />

  <ForbesDNA />

  <FAQAccordionV2 />
</template>

<style>
  .carousel__viewport {
    width: 100%;
  }
</style>